import React from 'react';
import { Link } from 'react-router-dom';
import { useButtonLinks } from '../hooks/useButtonLinks';
import { useTranslation } from '../hooks/useTranslation';

const Platforms = () => {
  const { handleButtonClick } = useButtonLinks();
  const { t } = useTranslation();
  return (
    <div className="platforms-page">
      {/* Hero Section */}
      <section className="platforms-hero">
        <div className="container">
          <div className="platforms-hero-content">
            <h1 className="platforms-title">{t('platforms.hero.title')}</h1>
            <p className="platforms-subtitle">
              {t('platforms.hero.subtitle')}
            </p>
            <div className="hero-question">
              <h2>{t('platforms.hero.question')}</h2>
            </div>
            <p className="hero-description">
              {t('platforms.hero.description')}
            </p>
          </div>
          <div className="platforms-hero-image">
            <div className="mt5-logo">
              <i className="fas fa-chart-line"></i>
              <span>MT5</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why Trade Section */}
      <section className="why-trade">
        <div className="container">
          <h2 className="section-title">{t('platforms.whyTrade.title')}</h2>
          <div className="features-grid">
            <div className="feature-item">
              <i className="fas fa-user-friendly"></i>
              <p>{t('platforms.whyTrade.features.userFriendly')}</p>
            </div>
            <div className="feature-item">
              <i className="fas fa-tools"></i>
              <p>{t('platforms.whyTrade.features.innovativeTools')}</p>
            </div>
            <div className="feature-item">
              <i className="fas fa-shield-alt"></i>
              <p>{t('platforms.whyTrade.features.security')}</p>
            </div>
            <div className="feature-item">
              <i className="fas fa-headset"></i>
              <p>{t('platforms.whyTrade.features.support')}</p>
            </div>
            <div className="feature-item">
              <i className="fas fa-mobile-alt"></i>
              <p>{t('platforms.whyTrade.features.flexibility')}</p>
            </div>
            <div className="feature-item">
              <i className="fas fa-dollar-sign"></i>
              <p>{t('platforms.whyTrade.features.lowDeposit')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Download Steps Section */}
      <section className="download-steps">
        <div className="container">
          <h2 className="section-title">{t('platforms.downloadSteps.title')}</h2>
          <div className="platforms-grid">
            <div className="platform-card">
              <div className="platform-icon">
                <i className="fas fa-desktop"></i>
              </div>
              <h3>{t('platforms.downloadSteps.pc.title')}</h3>
              <ol className="steps-list">
                <li>{t('platforms.downloadSteps.pc.step1')}</li>
                <li>{t('platforms.downloadSteps.pc.step2')}</li>
                <li>{t('platforms.downloadSteps.pc.step3')}</li>
              </ol>
              <button 
                className="btn btn-primary"
                onClick={(e) => handleButtonClick('downloadMT5', e)}
              >
                {t('platforms.downloadSteps.pc.button')}
              </button>
            </div>
            
            <div className="platform-card">
              <div className="platform-icon">
                <i className="fab fa-android"></i>
              </div>
              <h3>{t('platforms.downloadSteps.android.title')}</h3>
              <ol className="steps-list">
                <li>{t('platforms.downloadSteps.android.step1')}</li>
                <li>{t('platforms.downloadSteps.android.step2')}</li>
                <li>{t('platforms.downloadSteps.android.step3')}</li>
              </ol>
              <button 
                className="btn btn-primary"
                onClick={(e) => handleButtonClick('downloadMT5', e)}
              >
                {t('platforms.downloadSteps.android.button')}
              </button>
            </div>
            
            <div className="platform-card">
              <div className="platform-icon">
                <i className="fab fa-apple"></i>
              </div>
              <h3>{t('platforms.downloadSteps.ios.title')}</h3>
              <ol className="steps-list">
                <li>{t('platforms.downloadSteps.ios.step1')}</li>
                <li>{t('platforms.downloadSteps.ios.step2')}</li>
                <li>{t('platforms.downloadSteps.ios.step3')}</li>
              </ol>
              <button 
                className="btn btn-primary"
                onClick={(e) => handleButtonClick('downloadMT5', e)}
              >
                {t('platforms.downloadSteps.ios.button')}
              </button>
            </div>
          </div>
          
          <div className="no-account">
            <p>{t('platforms.downloadSteps.noAccount')}</p>
            <button 
              className="btn btn-secondary"
              onClick={(e) => handleButtonClick('openAccount', e)}
            >
              {t('platforms.downloadSteps.createAccount')}
            </button>
          </div>
        </div>
      </section>

      {/* Trading Instruments */}
      <section className="trading-instruments">
        <div className="container">
          <h2 className="section-title">{t('platforms.tradingInstruments.title')}</h2>
          <div className="instruments-grid">
            <div className="instrument-card">
              <div className="instrument-icon">
                <i className="fas fa-exchange-alt"></i>
              </div>
              <h3>{t('platforms.tradingInstruments.forex')}</h3>
            </div>
            <div className="instrument-card">
              <div className="instrument-icon">
                <i className="fas fa-chart-bar"></i>
              </div>
              <h3>{t('platforms.tradingInstruments.indices')}</h3>
            </div>
            <div className="instrument-card">
              <div className="instrument-icon">
                <i className="fas fa-building"></i>
              </div>
              <h3>{t('platforms.tradingInstruments.shares')}</h3>
            </div>
            <div className="instrument-card">
              <div className="instrument-icon">
                <i className="fas fa-coins"></i>
              </div>
              <h3>{t('platforms.tradingInstruments.commodities')}</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Account Types */}
      <section className="account-types">
        <div className="container">
          <h2 className="section-title">{t('platforms.accountTypes.title')}</h2>
          <div className="accounts-cards">
            
            {/* Pro Account */}
            <div className="account-card">
              <div className="account-header">
                <h3 className="account-name">{t('platforms.accountTypes.pro.name')}</h3>
                <div className="account-price">
                  <span className="deposit-amount">$20</span>
                  <span className="deposit-label">{t('platforms.accountTypes.pro.deposit')}</span>
                </div>
              </div>
              <div className="account-features">
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.spreads')}</span>
                  <span className="feature-value">1 pips</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.commission')}</span>
                  <span className="feature-value">$0</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.leverage')}</span>
                  <span className="feature-value">1:400</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.platform')}</span>
                  <span className="feature-value">MT5</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.islamic')}</span>
                  <span className="feature-value">{t('accountTypes.labels.available')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.execution')}</span>
                  <span className="feature-value">{t('accountTypes.labels.market')}</span>
                </div>
              </div>
              <Link to="/accounts/pro" className="btn btn-primary account-btn">{t('platforms.accountTypes.pro.button')}</Link>
            </div>

            {/* Prime Account */}
            <div className="account-card featured">
              <div className="popular-badge">{t('platforms.accountTypes.prime.popular')}</div>
              <div className="account-header">
                <h3 className="account-name">{t('platforms.accountTypes.prime.name')}</h3>
                <div className="account-price">
                  <span className="deposit-amount">$250</span>
                  <span className="deposit-label">{t('platforms.accountTypes.prime.deposit')}</span>
                </div>
              </div>
              <div className="account-features">
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.spreads')}</span>
                  <span className="feature-value">0 pips</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.commission')}</span>
                  <span className="feature-value">$6</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.leverage')}</span>
                  <span className="feature-value">1:400</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.platform')}</span>
                  <span className="feature-value">MT5</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.islamic')}</span>
                  <span className="feature-value">{t('accountTypes.labels.available')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.execution')}</span>
                  <span className="feature-value">{t('accountTypes.labels.market')}</span>
                </div>
              </div>
              <Link to="/accounts/prime" className="btn btn-primary account-btn">{t('platforms.accountTypes.pro.button')}</Link>
            </div>

            {/* Premium Account */}
            <div className="account-card">
              <div className="account-header">
                <h3 className="account-name">{t('platforms.accountTypes.premium.name')}</h3>
                <div className="account-price">
                  <span className="deposit-amount">$3000</span>
                  <span className="deposit-label">{t('platforms.accountTypes.premium.deposit')}</span>
                </div>
              </div>
              <div className="account-features">
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.spreads')}</span>
                  <span className="feature-value">0 pips</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.commission')}</span>
                  <span className="feature-value">$3</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.leverage')}</span>
                  <span className="feature-value">1:400</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.platform')}</span>
                  <span className="feature-value">MT5</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.islamic')}</span>
                  <span className="feature-value">{t('accountTypes.labels.available')}</span>
                </div>
                <div className="feature-row">
                  <span className="feature-label">{t('platforms.accountTypes.pro.execution')}</span>
                  <span className="feature-value">{t('accountTypes.labels.market')}</span>
                </div>
              </div>
              <Link to="/accounts/premium" className="btn btn-primary account-btn">{t('platforms.accountTypes.pro.button')}</Link>
            </div>

          </div>
          
          <div className="demo-account">
            <h3>{t('platforms.accountTypes.demo.title')}</h3>
            <p>{t('platforms.accountTypes.demo.description')}</p>
            <button 
              className="btn btn-secondary"
              onClick={(e) => handleButtonClick('tryDemo', e)}
            >
              {t('platforms.accountTypes.demo.button')}
            </button>
          </div>
        </div>
      </section>

      {/* Quick Help Section */}
      <section className="quick-help">
        <div className="container">
          <h2 className="section-title">{t('platforms.quickHelp.title')}</h2>
          <div className="help-sections">
            <div className="help-column">
              <h3>{t('platforms.quickHelp.experienced.title')}</h3>
              <div className="help-items">
                <div className="help-item">
                  <h4>{t('platforms.quickHelp.experienced.partner.title')}</h4>
                  <p>{t('platforms.quickHelp.experienced.partner.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('platforms.quickHelp.experienced.markets.title')}</h4>
                  <p>{t('platforms.quickHelp.experienced.markets.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('platforms.quickHelp.experienced.events.title')}</h4>
                  <p>{t('platforms.quickHelp.experienced.events.description')}</p>
                </div>
              </div>
            </div>
            
            <div className="help-column">
              <h3>{t('platforms.quickHelp.newTrader.title')}</h3>
              <div className="help-items">
                <div className="help-item">
                  <h4>{t('platforms.quickHelp.newTrader.platform.title')}</h4>
                  <p>{t('platforms.quickHelp.newTrader.platform.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('platforms.quickHelp.newTrader.charts.title')}</h4>
                  <p>{t('platforms.quickHelp.newTrader.charts.description')}</p>
                </div>
                <div className="help-item">
                  <h4>{t('platforms.quickHelp.newTrader.demo.title')}</h4>
                  <p>{t('platforms.quickHelp.newTrader.demo.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Platforms;